<script setup lang="ts">
import { ref, computed } from "vue";
import { useSortExperiences } from "~/composables/useSortExperiences";
import type { Portfolio } from "~/typings/portfolio";
import { useRouter } from "vue-router";
import { useResumeStore } from "~/stores/useResumeStore";
import { useUiStore } from "~/stores/useUiStore";
import {
  extractAvailableTags,
  filterExperiencesByTags,
} from "./Projects.logic";
import ExperienceTimeline from "./ExperienceTimeline/index.vue";
import Footer from "~/components/ui/Footer/Footer.vue";
import { UiPageWrapper, UiTitle } from "#components";

interface Props {
  experiences: Portfolio.Experience[];
}

const props = defineProps<Props>();
const router = useRouter();

// Get resume data and version for footer
const resumeStore = useResumeStore();
const uiStore = useUiStore();
const personalInformation = computed(() => resumeStore.personalInformation);
const version = computed(() => uiStore.packageVersion);

// Sort experiences chronologically (reactive)
const sortedExperiences = computed(() =>
  useSortExperiences(props.experiences ?? [])
);

// Extract unique tags from all experiences
const availableTags = computed(() => extractAvailableTags(props.experiences));

type FilterTab = "all" | string;
const activeFilter = ref<FilterTab>("all");

const filterTabs = computed(() => [
  { label: "All", value: "all" as FilterTab },
  ...availableTags.value.map((tag) => ({
    label: tag.replace(/-/g, " "),
    value: tag as FilterTab,
  })),
]);

const setFilter = (tab: FilterTab) => {
  activeFilter.value = tab;
};

const filteredExperiences = computed(() =>
  filterExperiencesByTags(sortedExperiences.value, activeFilter.value)
);

const openExperienceDetails = (experience: Portfolio.Experience) => {
  router.push(`/projects/${experience.id}`);
};
</script>

<template>
  <UiPageWrapper>
    <div class="py-10 mb-4">
      <UiTitle
        level="1"
        class="text-5xl md:text-7xl leading-16 md:leading-24 font-urbanist font-bold"
      >
        Projects & Experience
      </UiTitle>
      <UiParagraph
        class="text-lg md:text-2xl font-medium text-vshadow-sm shadow-secondary-900 leading-relaxed"
      >
        A comprehensive timeline of my professional journey, showcasing the
        diverse range of projects and experiences that have shaped my career.
      </UiParagraph>
    </div>

    <div class="mb-16">
      <UiTitle level="4" class="text-2xl font-semibold text-left mb-4">
        Filter by Project Type
      </UiTitle>
      <div
        role="tablist"
        aria-label="Filter projects by type"
        class="mb-12 flex flex-wrap gap-3"
      >
        <UiButton
          v-for="tab in filterTabs"
          :key="tab.value"
          role="tab"
          :aria-selected="activeFilter === tab.value"
          color="primary"
          :variant="activeFilter === tab.value ? 'solid' : 'outline'"
          class="capitalize"
          @click="setFilter(tab.value)"
          @keydown.enter="setFilter(tab.value)"
          @keydown.space.prevent="setFilter(tab.value)"
        >
          {{ tab.label }}
        </UiButton>
      </div>
    </div>

    <div>
      <ExperienceTimeline
        :experiences="filteredExperiences"
        @select-experience="openExperienceDetails"
      />
    </div>
  </UiPageWrapper>

  <Footer
    v-if="personalInformation?.location"
    :location="personalInformation.location"
    :email="personalInformation.email"
    :version="version"
  />
</template>
